import { ApiService } from '../../../config';
import type { Review, ReviewStatus } from '../types';

/** Avaliação como o backend devolve em GET /reviews/profile/{id}. */
export type ApiReview = {
  externalId: string;
  reply?: string | null;
  reviewerName?: string | null;
  reviewerPhoto?: string | null;
  starRating?: string | null;
  comment?: string | null;
  status?: ReviewStatus | null;
  createTime?: string | null;
  repliedAt?: string | null;
  replySource?: 'AI' | 'HUMAN' | null;
  errorMsg?: string | null;
};

/** Como responder às próximas avaliações do perfil. */
export type ReplyInstructions = {
  instructions: string | null;
  preferredWords: string[];
};

const ESTRELAS: Record<string, number> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
};

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * O perfil do cliente chega de dois jeitos: "publicId - nome" (vindo do
 * /users/me) ou só o publicId (depois de escolher em /select-profile).
 */
export const profileIdDoUsuario = (bruto?: string): string =>
  bruto?.match(UUID)?.[0] ?? bruto?.trim() ?? '';

export const starsToNumber =(starRating?: string | null): number => {
  const texto = String(starRating ?? '').trim().toUpperCase();
  return ESTRELAS[texto] ?? (Number(texto) || 0);
};

/**
 * Só conta como respondida a que já está no Google (REPLIED). Em WAITING_REPLY
 * o texto existe, mas é o rascunho da IA esperando o envio: vira `draft`.
 */
export const toReview = (api: ApiReview): Review => {
  const texto = api.reply?.trim() ? api.reply : undefined;
  const respondida = api.status === 'REPLIED' && !!texto;
  return {
    id: api.externalId,
    authorName: api.reviewerName?.trim() || 'Cliente',
    authorPhoto: api.reviewerPhoto || undefined,
    rating: starsToNumber(api.starRating),
    comment: api.comment || '',
    date: api.createTime || '',
    profileName: '',
    status: api.status ?? (respondida ? 'REPLIED' : 'WAITING_REPLY'),
    draft: respondida ? undefined : texto,
    errorMsg: api.errorMsg || undefined,
    response: respondida
      ? {
          id: api.externalId,
          text: texto as string,
          date: api.repliedAt || '',
          source: api.replySource ?? null,
        }
      : undefined,
  };
};

const data = <T>(response: { data?: { data?: T } }): T =>
  response.data?.data as T;

const idNaUrl = (reviewId: string) => encodeURIComponent(reviewId);

export const mensagemDeErro = (error: unknown, fallback: string): string => {
  const resposta = (
    error as { response?: { data?: { userMessage?: string; message?: string } } }
  )?.response?.data;
  return (
    resposta?.userMessage ||
    resposta?.message ||
    (error instanceof Error ? error.message : '') ||
    fallback
  );
};

export const reviewsService = {
  async list(profileId: string): Promise<Review[]> {
    const lista = data<ApiReview[]>(
      await ApiService.get(`/reviews/profile/${profileId}`),
    );
    return (lista ?? []).map(toReview);
  },

  /** Publica (ou substitui) a resposta no Google. */
  async saveReply(
    reviewId: string,
    comment: string,
    source: 'AI' | 'HUMAN',
  ): Promise<void> {
    await ApiService.put(`/reviews/${idNaUrl(reviewId)}/reply`, {
      comment,
      source,
    });
  },

  async deleteReply(reviewId: string): Promise<void> {
    await ApiService.delete(`/reviews/${idNaUrl(reviewId)}/reply`);
  },

  /**
   * Sugestão da IA seguindo a instrução do perfil. Com `correction`, refaz a
   * resposta publicada corrigindo o erro descrito.
   */
  async suggestReply(reviewId: string, correction?: string): Promise<string> {
    const texto = data<string>(
      await ApiService.post(
        `/reviews/${idNaUrl(reviewId)}/reply/suggestion`,
        correction?.trim() ? { correction: correction.trim() } : {},
      ),
    );
    return texto || '';
  },

  async getInstructions(profileId: string): Promise<ReplyInstructions> {
    const atual = data<ReplyInstructions>(
      await ApiService.get(`/reviews/profile/${profileId}/instructions`),
    );
    return {
      instructions: atual?.instructions ?? null,
      preferredWords: atual?.preferredWords ?? [],
    };
  },

  async saveInstructions(
    profileId: string,
    instrucoes: { instructions: string; preferredWords: string[] },
  ): Promise<ReplyInstructions> {
    const salvo = data<ReplyInstructions>(
      await ApiService.put(
        `/reviews/profile/${profileId}/instructions`,
        instrucoes,
      ),
    );
    return {
      instructions: salvo?.instructions ?? null,
      preferredWords: salvo?.preferredWords ?? [],
    };
  },
};
